import { Tag, message } from "antd";
import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useMemo, useState } from "react";
import "./styles.css";

import API from "../../../services";
import HeaderFarms from "../../../utils/header_farms";
import moment from "moment";
import { Report } from "notiflix";
import { useNavigate, useParams } from "react-router";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { loginRequest } from "../../../config/api.config";
import { requestToken } from "../../../auth/AuthServices";

const isEmpty = (v) => {
    if (v === null || v === undefined) return true;
    if (typeof v === "string" && v.trim() === "") return true;
    if (Array.isArray(v) && v.length === 0) return true;
    return false;
};

function PrintServiceRequest() {
    const { id } = useParams();
    const navigate = useNavigate();

    const isAuthenticated = useIsAuthenticated();
    const { instance } = useMsal();

    const [applications, setApplications] = useState([]);
    const [all, setAll] = useState(null);

    const mapApiToForm = (apiRes) => {
        const raw = apiRes?.data?.data ?? apiRes?.data ?? {};
        const approval = raw?.approval_process_id?.data ?? raw?.approval_process_id ?? {};

        return {
            ...raw,
            nik: raw.employee_id || raw.nik,
            company: raw.company,
            account_request: raw.account_request ?? [],
            system_request: raw.system_request ?? [],
            permission_network_folder: raw.permission_network_folder ?? [],
            communication_access: raw.communication_access ?? [],
            approval_process_id: {
                id: approval?.id || approval?._id,
                _id: approval?._id,
                status: approval?.status,
                approval_key: approval?.approval_key,
                created_by: approval?.created_by,
                detail: (approval?.detail ?? []).map((d) => ({
                    _id: d._id,
                    status: !!d.status,
                    rejected: !!d.rejected,
                    approver: d.approver ?? null,
                    approved_time: d.approved_time ?? null,
                })),
                comments: approval?.comments ?? [],
            },
        };
    };

    const getApplications = (token) => {
        API.getApplications(HeaderFarms(token), null)
            .then((res) => {
                const DTO = (res.data ?? []).map((i) => ({
                    id: i.app_id,
                    name: i.name,
                }));
                setApplications(DTO);
            })
            .catch((err) => {
                console.log(err);
                message.warning("Failed to load application list");
            });
    };

    const getDetailSR = (token) => {
        API.SRView(HeaderFarms(token), id)
            .then((res) => {
                const mapped = mapApiToForm(res);
                setAll(mapped);
                setTimeout(() => window.print(), 500);
            })
            .catch((err) => {
                console.error(err);
                Report.info("Service Request", "Sorry, Service Request not found", "Okay");
            });
    };

    function handleLogin(instance) {
        instance.loginRedirect(loginRequest).catch((e) => console.error(e));
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const accessToken = await requestToken();
                if (!accessToken) {
                    handleLogin(instance);
                    return;
                }
                getDetailSR(accessToken);
                getApplications(accessToken);
            } catch (error) {
                handleLogin(instance);
                navigate("/");
                console.error(error);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    const systemNames = useMemo(() => {
        if (!all) return [];
        const ids = all.system_request ?? [];
        return ids
            .map((sid) => applications.find((a) => a.id === sid)?.name || sid)
            .filter(Boolean);
    }, [all, applications]);

    const renderTagList = (items) => (
        <span>
            {items.map((it, idx) => (
                <Tag key={idx} style={{ marginBottom: 2, fontSize: 10 }}>
                    {it}
                </Tag>
            ))}
        </span>
    );

    const buildItems = (rows) => rows.filter((r) => !isEmpty(r.value));

    const renderSection = (title, items, columns = 2) => {
        if (items.length === 0) return null;

        const rows = [];
        for (let i = 0; i < items.length; i += columns) {
            rows.push(items.slice(i, i + columns));
        }

        return (
            <div className="print-section">
                <h5 className="print-section-title">{title}</h5>
                <table className="print-table">
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri}>
                                {row.map((item, ci) => (
                                    <React.Fragment key={ci}>
                                        <th className="print-label">{item.label}</th>
                                        <td className="print-value">{item.value}</td>
                                    </React.Fragment>
                                ))}
                                {row.length < columns &&
                                    Array.from({ length: columns - row.length }).map((_, idx) => (
                                        <React.Fragment key={`empty-${idx}`}>
                                            <th className="print-label print-label--empty" />
                                            <td className="print-value print-value--empty" />
                                        </React.Fragment>
                                    ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    if (!all) {
        return (
            <div className="print-container">
                <div className="text-center" style={{ marginTop: 40 }}>Loading...</div>
            </div>
        );
    }

    const userItems = buildItems([
        { label: "Fullname", value: all.fullname },
        { label: "NIK", value: all.nik },
        { label: "Position", value: all.position },
        { label: "Department", value: all.department },
        { label: "Business Unit", value: all.company },
        { label: "Phone", value: all.phone_number },
        { label: "Employee Status", value: all.employee_status },
    ]);

    const equipmentItems = buildItems([
        { label: "Type of Request", value: all.type_request },
        { label: "Type Explain", value: all.type_request_explain },
        { label: "Equipment", value: all.equipment_details },
        { label: "Equipment Other", value: all.equipment_details_explain },
    ]);

    const droneItems = buildItems([
        { label: "Area", value: all.drone_area },
        {
            label: "Tanggal & Jam",
            value: all.drone_request_at ? moment(all.drone_request_at).format("LLL") : null,
        },
    ]);

    const systemItems = buildItems([
        {
            label: "Account",
            value: !isEmpty(all.account_request) ? renderTagList(all.account_request) : null,
        },
        { label: "Account Other", value: all.account_request_other },
        {
            label: "System",
            value: !isEmpty(systemNames) ? renderTagList(systemNames) : null,
        },
        { label: "System Other", value: all.system_request_other },
        { label: "Justification", value: all.justification },
    ]);

    const networkItems = buildItems([
        { label: "Login Name", value: all.login_name },
        { label: "Email Address", value: all.email_address },
        { label: "Network Folder", value: all.network_folder },
        {
            label: "Permissions",
            value: !isEmpty(all.permission_network_folder)
                ? renderTagList(all.permission_network_folder)
                : null,
        },
        {
            label: "Communication Access",
            value: !isEmpty(all.communication_access)
                ? renderTagList(all.communication_access)
                : null,
        },
    ]);

    const descriptionItems = buildItems([
        { label: "Description", value: all.description },
    ]);

    const approvalItems = (all?.approval_process_id?.detail ?? []).map((d, idx) => ({
        label: d?.approver?.fullname ?? `Approver ${idx + 1}`,
        value: d?.status
            ? `Approved${d?.approved_time ? " - " + moment(d.approved_time).format("LLL") : ""}`
            : d?.rejected
                ? "Rejected"
                : "Waiting Approval",
    }));

    return (
        <div className="print-container">
            <div className="text-center" style={{ marginBottom: 8 }}>
                <h4 style={{ margin: 0, fontWeight: 700 }}>Service Request Form</h4>
                <div style={{ fontSize: 11, color: "#666" }}>Division: Information Technology</div>
            </div>

            <div className="print-meta">
                <div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{all?.form_record}</div>
                    {all?.created_at && (
                        <div style={{ fontSize: 10, color: "#666" }}>
                            Created: {moment(all.created_at).format("LLL")}
                        </div>
                    )}
                </div>
                <div className="text-end">
                    <div style={{ fontSize: 11 }}>
                        Status: <b>{all?.approval_process_id?.status}</b>
                    </div>
                </div>
            </div>

            {renderSection("User Requiring Access", userItems, 2)}
            {renderSection("Equipment Request", equipmentItems, 2)}
            {renderSection("Drone Request", droneItems, 2)}
            {renderSection("System Request", systemItems, 1)}
            {renderSection("Network Access and Communication Request", networkItems, 2)}
            {renderSection("Description", descriptionItems, 1)}
            {renderSection("Approval Status", approvalItems, 1)}

            {all?.approval_process_id?.approval_key && (
                <div className="text-center" style={{ marginTop: 16, pageBreakInside: "avoid" }}>
                    <QRCodeSVG
                        size={80}
                        value={`${window.location.origin}/wf/approved/${all.approval_process_id.approval_key}`}
                    />
                    <div style={{ fontSize: 10, marginTop: 2, color: "#666" }}>
                        Approval QR Code
                    </div>
                </div>
            )}
        </div>
    );
}

export default PrintServiceRequest;
